import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

// Helper to load all users from localStorage
const loadUsersFromStorage = () => {
  try {
    const users = localStorage.getItem("chess_users");
    return users ? JSON.parse(users) : {};
  } catch (e) {
    console.error("Failed to load chess users from localStorage", e);
    return {};
  }
};

// Helper to save all users to localStorage
const saveUsersToStorage = (users) => {
  try {
    localStorage.setItem("chess_users", JSON.stringify(users));
  } catch (e) {
    console.error("Failed to save chess users to localStorage", e);
  }
};

// Helper to save current user session
const saveCurrentUserSession = (user) => {
  try {
    if (user) {
      localStorage.setItem("chess_current_user", JSON.stringify(user));
    } else {
      localStorage.removeItem("chess_current_user");
    }
  } catch (e) {
    console.error("Failed to save current user session", e);
  }
};

// Load initial current user session
const getInitialCurrentUser = () => {
  try {
    const user = localStorage.getItem("chess_current_user");
    return user ? JSON.parse(user) : null;
  } catch (e) {
    return null;
  }
};

const initialState = {
  currentUser: getInitialCurrentUser(),
  error: null,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    login: (state, action) => {
      const { username, password } = action.payload;
      const normalizedUsername = username.trim().toLowerCase();
      const users = loadUsersFromStorage();

      const user = users[normalizedUsername];
      if (!user) {
        state.error = "Username tidak ditemukan.";
        return;
      }

      if (user.password !== password) {
        state.error = "Password salah.";
        return;
      }

      // Successful login
      state.currentUser = user;
      state.error = null;
      saveCurrentUserSession(user);
    },

    register: (state, action) => {
      const { username, password } = action.payload;
      const trimmedUsername = username.trim();
      const normalizedUsername = trimmedUsername.toLowerCase();
      
      if (!trimmedUsername || !password) {
        state.error = "Username dan password wajib diisi.";
        return;
      }

      const users = loadUsersFromStorage();
      if (users[normalizedUsername]) {
        state.error = "Username sudah terdaftar.";
        return;
      }

      // Create new user template
      const newUser = {
        username: trimmedUsername,
        password: password,
        elo: 1200,
        stats: {
          wins: 0,
          losses: 0,
          draws: 0,
          regulerWins: 0,
          hardWins: 0,
          hellWins: 0,
        },
        history: [],
      };

      // Save user
      users[normalizedUsername] = newUser;
      saveUsersToStorage(users);

      // Log in immediately
      state.currentUser = newUser;
      state.error = null;
      saveCurrentUserSession(newUser);
    },

    logout: (state) => {
      state.currentUser = null;
      state.error = null;
      saveCurrentUserSession(null);
    },

    clearError: (state) => {
      state.error = null;
    },

    recordMatch: (state, action) => {
      if (!state.currentUser) return;

      const { difficulty, outcome } = action.payload; // outcome: 'win' | 'loss' | 'draw'
      const normalizedUsername = state.currentUser.username.toLowerCase();
      const users = loadUsersFromStorage();
      const user = users[normalizedUsername];

      if (!user) return;

      // Elo logic: reguler (1200), hard (1600), hell (2000)
      const userElo = user.elo || 1200;
      let opponentElo = 1600; // default for hard
      if (difficulty === "reguler") opponentElo = 1200;
      if (difficulty === "hell") opponentElo = 2000;

      // Outcome score: win = 1, loss = 0, draw = 0.5
      let S = 0.5;
      if (outcome === "win") S = 1;
      if (outcome === "loss") S = 0;

      // Expected score
      const E = 1 / (1 + Math.pow(10, (opponentElo - userElo) / 400));
      const K = 32;
      const eloChange = Math.round(K * (S - E));
      const newElo = Math.max(100, userElo + eloChange);

      // Update stats
      user.elo = newElo;
      if (!user.stats) {
        user.stats = { wins: 0, losses: 0, draws: 0, regulerWins: 0, hardWins: 0, hellWins: 0 };
      }
      if (outcome === "win") {
        user.stats.wins += 1;
        if (difficulty === "reguler") user.stats.regulerWins = (user.stats.regulerWins || 0) + 1;
        else if (difficulty === "hard") user.stats.hardWins = (user.stats.hardWins || 0) + 1;
        else if (difficulty === "hell") user.stats.hellWins = (user.stats.hellWins || 0) + 1;
      } else if (outcome === "loss") {
        user.stats.losses += 1;
      } else {
        user.stats.draws += 1;
      }

      // Add to match history
      const matchRecord = {
        id: uuidv4(),
        date: new Date().toISOString(),
        difficulty,
        outcome,
        eloChange: eloChange >= 0 ? `+${eloChange}` : `${eloChange}`,
        newElo,
      };
      
      user.history = [matchRecord, ...(user.history || [])];

      // Save user profile back to storage
      users[normalizedUsername] = user;
      saveUsersToStorage(users);

      // Update Redux state
      state.currentUser = user;
      saveCurrentUserSession(user);
    },
  },
});

export const { login, register, logout, clearError, recordMatch } = userSlice.actions;

export const selectCurrentUser = (state) => state.user.currentUser;
export const selectUserError = (state) => state.user.error;

export default userSlice.reducer;
